/**
 * What a Trial Master File checklist row says about the document it holds.
 *
 * The TMF checklist has been able to hold documents since 2026-08-24 — files
 * attach to `projects/{projectId}/essentialDocs/{docId}/attachments/{id}`, one
 * of them `current`, the ones it replaced retained rather than deleted. All of
 * that was server-side and none of it was visible. This module turns one
 * checklist entry plus its attachments into the strings a row renders, and it
 * is the only place that decision is made in the browser.
 *
 * Pure. No React, no DOM, no Firebase — so it is testable from the `functions`
 * package's runner (`cd functions && npm run test:web`), which is how the
 * frontend's logic gets tested at all: `web` has no test runner and must not
 * gain one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A DELIBERATE PORT, AND THESE TWO COPIES MUST STAY IN STEP:
 *
 *   • the classification  — `functions/src/data/essentialDocs.ts` (`scope`)
 *   • the refusal wording — `functions/src/tmf/attachments.ts` (`canAttach`)
 *
 * Change either of those and `TMF_CHECKLIST_SCOPES` / `attachmentRefusal()`
 * below must change with it.
 *
 * WHY A PORT RATHER THAN A CALL. `scope` is deliberately NOT stored in
 * Firestore — see the comment in `initializeTMFHandler`: a stored copy goes
 * stale the moment a classification is corrected in source, and Firestore rules
 * let anyone with `edit` write fields on that collection, so a stored copy
 * would be client-writable. The browser therefore cannot read the scope off the
 * document, and it cannot import the module either, because the web bundle does
 * not compile the `functions/` tree. The alternative was widening
 * `getEssentialDocsV2` to return a verdict per row — a server change, a deploy,
 * and a second authorizing surface for something that is only ever display.
 *
 * WHY THE COPY IS SAFE ANYWAY. It is not trusted, it is CHECKED.
 * `tmfAttachments.test.ts` imports BOTH implementations and requires them to
 * agree on all 37 checklist entries, resolved by `templateId` and again by
 * `name`, plus the off-checklist shapes. A drifted copy fails CI rather than
 * quietly telling a user that a patient record is "not filed yet".
 *
 * AND IT IS NOT A SECURITY BOUNDARY. `attachDocument` and
 * `getVersionDownloadUrl` both re-run the real `canAttach` server-side against
 * the STORED identity. Nothing here can permit anything; a drifted copy is a
 * display defect, which is exactly why the display copy is allowed to exist.
 *
 * Same pattern, and the same reasoning, as `scheduleConfirmationStatus.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type TmfScope = 'platform' | 'conduct' | 'unclassified';

/** The identifying fields a stored `essentialDocs` document carries. */
export interface TmfChecklistEntryLike {
  /** Present on projects initialized after 2026-08-24. Absent, not empty, before that. */
  templateId?: string | null;
  /** Present on every stored document, frozen at whatever source said that day. */
  name?: string | null;
}

/** An attachment document as it comes back out of Firestore. */
export interface TmfAttachmentLike {
  id?: string | null;
  filename?: string | null;
  /** `"uploaded"` or `"generated"`. Anything else reads as uploaded, as it does server-side. */
  source?: string | null;
  versionLabel?: string | null;
  /** The uid the attachment is recorded against. */
  attachedBy?: string | null;
  attachedAt?: unknown;
  supersededAt?: unknown;
  current?: boolean;
}

export type TmfHoldingState =
  /** The entry holds a file, and it can be downloaded. */
  | 'holds'
  /** The entry may hold a file and holds none — a document the study still needs. */
  | 'empty'
  /** The entry may not hold a file at all. `reason` says why, in prose. */
  | 'refused';

export interface TmfHolding {
  state: TmfHoldingState;
  /** The file's name, or `''`. Never undefined — a row renders these directly. */
  filename: string;
  /** e.g. `"12 Mar 2026"`, or `''` when the record carries no readable date. */
  attachedOn: string;
  /** A display name where one is known, the recorded uid otherwise, `''` if neither. */
  attributedTo: string;
  /** `"Uploaded"` or `"Generated"`, or `''`. */
  sourceLabel: string;
  /** The sponsor's own label ("v3.0"), or `''` — absent is entirely normal. */
  versionLabel: string;
  /** The supporting line: provenance when it holds a file, the refusal when it may not. */
  detail: string;
  /** Why this entry may not hold a file. Non-empty exactly when state is `refused`. */
  reason: string;
  canDownload: boolean;
  /**
   * What to pass as `versionId` to `getVersionDownloadUrl`. The literal
   * `"current"` sentinel, so the row need not know the attachment's id and
   * cannot ask for a file that has since been replaced.
   */
  downloadVersionId: string | null;
  /** How many superseded attachments this entry still retains behind the current one. */
  earlierVersionCount: number;
  /** A sentence naming that count, or `''` when there are none. */
  earlierVersionNote: string;
}

export interface DescribeHoldingOptions {
  /** uid → display name, e.g. built from the project's members. */
  people?: Record<string, string>;
}

/**
 * The checklist, mirrored from `functions/src/data/essentialDocs.ts`.
 *
 * Order, ids, names and scopes are all asserted equal to that file's, so this
 * is a copy CI will not let drift. `name` is carried as well as `templateId`
 * because the refusal text quotes the checklist's own name, and because
 * projects initialized before 2026-08-24 have no `templateId` to key on.
 */
export const TMF_CHECKLIST_SCOPES: ReadonlyArray<{
  templateId: string;
  name: string;
  scope: TmfScope;
}> = [
  { templateId: 'investigators-brochure', name: "Investigator's Brochure (IB)", scope: 'platform' },
  { templateId: 'signed-protocol-and-amendments', name: 'Signed Protocol and Amendments', scope: 'platform' },
  { templateId: 'informed-consent-form', name: 'Informed Consent Form (ICF)', scope: 'platform' },
  { templateId: 'irb-approval', name: 'IRB/IEC Approval', scope: 'platform' },
  { templateId: 'irb-composition', name: 'IRB/IEC Composition', scope: 'platform' },
  { templateId: 'regulatory-authority-approval', name: 'Regulatory Authority Approval', scope: 'platform' },
  { templateId: 'investigator-cv', name: 'Investigator CV', scope: 'platform' },
  { templateId: 'medical-license', name: 'Medical License', scope: 'platform' },
  { templateId: 'form-fda-1572', name: 'Form FDA 1572', scope: 'platform' },
  { templateId: 'financial-disclosure', name: 'Financial Disclosure', scope: 'platform' },
  { templateId: 'delegation-of-authority-log', name: 'Delegation of Authority Log', scope: 'platform' },
  { templateId: 'staff-training-records', name: 'Staff Training Records', scope: 'platform' },
  { templateId: 'site-signature-log', name: 'Site Signature Log', scope: 'platform' },
  { templateId: 'laboratory-certifications', name: 'Laboratory Certifications', scope: 'platform' },
  { templateId: 'clinical-trial-agreement', name: 'Clinical Trial Agreement', scope: 'platform' },
  { templateId: 'insurance-indemnity', name: 'Insurance/Indemnity', scope: 'platform' },
  { templateId: 'ip-handling-instructions', name: 'IP Handling Instructions', scope: 'platform' },
  { templateId: 'subject-screening-log', name: 'Subject Screening Log', scope: 'conduct' },
  { templateId: 'subject-identification-log', name: 'Subject Identification Log', scope: 'conduct' },
  { templateId: 'subject-enrollment-log', name: 'Subject Enrollment Log', scope: 'conduct' },
  { templateId: 'signed-informed-consent-forms', name: 'Signed Informed Consent Forms', scope: 'conduct' },
  { templateId: 'source-documents', name: 'Source Documents', scope: 'conduct' },
  { templateId: 'case-report-forms', name: 'Case Report Forms (CRFs)', scope: 'conduct' },
  { templateId: 'ip-accountability-records', name: 'IP Accountability Records', scope: 'conduct' },
  { templateId: 'monitoring-visit-reports', name: 'Monitoring Visit Reports', scope: 'conduct' },
  { templateId: 'protocol-deviation-log', name: 'Protocol Deviation Log', scope: 'conduct' },
  { templateId: 'sae-reports', name: 'SAE Reports', scope: 'conduct' },
  { templateId: 'ib-updates', name: 'IB Updates', scope: 'platform' },
  { templateId: 'irb-continuing-review', name: 'IRB/IEC Continuing Review', scope: 'platform' },
  { templateId: 'protocol-amendments', name: 'Protocol Amendments', scope: 'platform' },
  { templateId: 'ip-accountability-summary', name: 'IP Accountability Summary', scope: 'conduct' },
  { templateId: 'site-closeout-documentation', name: 'Site Closeout Documentation', scope: 'conduct' },
  { templateId: 'final-monitoring-report', name: 'Final Monitoring Report', scope: 'conduct' },
  { templateId: 'clinical-study-report', name: 'Clinical Study Report', scope: 'conduct' },
  { templateId: 'irb-final-report', name: 'IRB/IEC Final Report', scope: 'conduct' },
  { templateId: 'regulatory-authority-final-report', name: 'Regulatory Authority Final Report', scope: 'conduct' },
  { templateId: 'subject-data-retention', name: 'Subject Data Retention', scope: 'conduct' },
];

/** Ask `getVersionDownloadUrl` for whatever file the entry holds now. */
export const CURRENT_ATTACHMENT = 'current';

function present(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';
}

type ResolvedBy = 'templateId' | 'name' | 'none';

/**
 * Port of `resolveStoredScope` in `functions/src/tmf/documentScope.ts`.
 *
 * `templateId` wins where it exists, because it is assigned once and never
 * changed; `name` is display text that has already been corrected in source
 * once. A templateId that is present but matches nothing does NOT fall back to
 * the name — it is a positive claim to be a row this build does not know.
 */
function resolveScope(entry: TmfChecklistEntryLike | null | undefined): {
  scope: TmfScope;
  resolvedBy: ResolvedBy;
  name: string;
} {
  const templateId = present(entry?.templateId);
  const storedName = present(entry?.name);

  if (templateId) {
    const found = TMF_CHECKLIST_SCOPES.find(d => d.templateId === templateId);
    return {
      scope: found ? found.scope : 'unclassified',
      resolvedBy: 'templateId',
      name: found ? found.name : storedName,
    };
  }

  if (storedName) {
    const found = TMF_CHECKLIST_SCOPES.find(d => d.name === storedName);
    return {
      scope: found ? found.scope : 'unclassified',
      resolvedBy: 'name',
      name: found ? found.name : storedName,
    };
  }

  return { scope: 'unclassified', resolvedBy: 'none', name: '' };
}

/** Whether this checklist entry may hold a file. Permits ONLY `platform`. */
export function scopeOfEntry(entry: TmfChecklistEntryLike | null | undefined): TmfScope {
  return resolveScope(entry).scope;
}

/**
 * Why this entry may not hold a file, or `null` when it may.
 *
 * The three strings below are copied verbatim from `canAttach` in
 * `functions/src/tmf/attachments.ts` and must be changed there and here
 * together. The test asserts they are identical for every entry, so a one-sided
 * edit fails CI rather than shipping two different explanations of the same
 * refusal.
 *
 * Note the shape of the test, which mirrors the guard's: it permits on
 * `platform` and refuses everything else, so an entry the checklist does not
 * recognize is refused rather than waved through.
 */
export function attachmentRefusal(entry: TmfChecklistEntryLike | null | undefined): string | null {
  const { scope, resolvedBy, name } = resolveScope(entry);

  if (scope === 'platform') return null;

  if (scope === 'conduct') {
    return (
      `"${name}" is a record of what happens to patients once the trial is ` +
      `running, so its files cannot be stored here. Keep them in your eTMF or ` +
      `EDC — this checklist entry tracks the document without holding it.`
    );
  }

  if (resolvedBy === 'none') {
    return (
      'This checklist entry is missing the details needed to identify it, so a ' +
      'file cannot be attached to it. Set the study\'s Trial Master File up again ' +
      'to rebuild the checklist.'
    );
  }

  const described = name ? `"${name}"` : 'This checklist entry';
  return (
    `${described} is not a document on this study's checklist, so a file cannot ` +
    `be attached to it. The checklist may have changed since this study was set up.`
  );
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Milliseconds for any of the date shapes an attachment record can carry, or
 * `null`.
 *
 * All four shapes are real. The Firestore client SDK hands back a `Timestamp`
 * with `toDate()`; the same document serialized through a callable arrives as
 * `{_seconds, _nanoseconds}`; `{seconds}` turns up where a record has been
 * round-tripped through JSON; and a plain `Date` or epoch number is what a
 * fixture or an older write leaves. Guessing wrong prints nothing at all, which
 * on a filing date looks like a document nobody filed.
 */
function toMillis(value: unknown): number | null {
  if (value == null) return null;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value !== 0 ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;

    if (typeof o.toDate === 'function') {
      const d = (o.toDate as () => unknown)();
      if (d instanceof Date && Number.isFinite(d.getTime())) return d.getTime();
    }

    const seconds = typeof o.seconds === 'number' ? o.seconds
      : typeof o._seconds === 'number' ? o._seconds
        : null;
    if (seconds !== null && Number.isFinite(seconds)) return seconds * 1000;
  }

  return null;
}

/**
 * A filing date a person can read, or `''` when the record carries none.
 *
 * Day precision on purpose: the question a checklist answers is which documents
 * the study holds, not what minute they arrived.
 */
export function formatAttachedAt(value: unknown): string {
  const ms = toMillis(value);
  if (ms === null) return '';
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * The file this entry holds now, or `null`.
 *
 * Reads the newest `current` record rather than asserting there is only one —
 * `attachmentStore.attachDocument` is where that invariant is enforced, inside
 * a transaction, and a reader's job is to answer the question rather than to
 * re-litigate it. Same choice, and the same reasoning, as `currentAttachment`
 * server-side.
 */
export function pickCurrentAttachment(
  attachments: ReadonlyArray<TmfAttachmentLike> | null | undefined,
): TmfAttachmentLike | null {
  if (!attachments || attachments.length === 0) return null;
  const live = attachments.filter(a => a && a.current === true);
  if (live.length === 0) return null;
  return live
    .slice()
    .sort((a, b) => (toMillis(b.attachedAt) ?? 0) - (toMillis(a.attachedAt) ?? 0))[0];
}

/** `"Generated"` or `"Uploaded"`. Coerces the same way `attachmentStore` does. */
function sourceLabelOf(source: unknown): string {
  return source === 'generated' ? 'Generated' : 'Uploaded';
}

const EMPTY: TmfHolding = {
  state: 'empty',
  filename: '',
  attachedOn: '',
  attributedTo: '',
  sourceLabel: '',
  versionLabel: '',
  detail: '',
  reason: '',
  canDownload: false,
  downloadVersionId: null,
  earlierVersionCount: 0,
  earlierVersionNote: '',
};

function earlierVersionNoteFor(count: number): string {
  if (count <= 0) return '';
  return count === 1
    ? '1 earlier version retained'
    : `${count} earlier versions retained`;
}

/**
 * Everything one checklist row needs to say about the document it holds.
 *
 * ORDER MATTERS, and it is the same order the server uses: the refusal is
 * decided BEFORE the attachments are looked at, so an entry that may not hold a
 * file is refused whether or not one is somehow there. `getVersionDownloadUrl`
 * makes the same choice for the same reason — the cost of being wrong is
 * showing a patient record.
 *
 * Every string field is a string. A row renders these directly, and a missing
 * `versionLabel` or `attachedBy` is entirely ordinary, so `undefined` must
 * never reach the page.
 */
export function describeHolding(
  entry: TmfChecklistEntryLike | null | undefined,
  attachments: ReadonlyArray<TmfAttachmentLike> | null | undefined,
  options?: DescribeHoldingOptions,
): TmfHolding {
  const refusal = attachmentRefusal(entry);
  if (refusal) {
    return { ...EMPTY, state: 'refused', reason: refusal, detail: refusal };
  }

  const all = (attachments ?? []).filter(Boolean);
  const current = pickCurrentAttachment(all);
  const earlierVersionCount = all.length - (current ? 1 : 0);
  const earlierVersionNote = earlierVersionNoteFor(earlierVersionCount);

  if (!current) {
    return {
      ...EMPTY,
      state: 'empty',
      detail: 'No document has been filed against this entry yet.',
      earlierVersionCount,
      earlierVersionNote,
    };
  }

  const attachedBy = present(current.attachedBy);
  const attributedTo = (options?.people ?? {})[attachedBy] ?? attachedBy;
  const attachedOn = formatAttachedAt(current.attachedAt);
  const sourceLabel = sourceLabelOf(current.source);

  const bits = [sourceLabel];
  if (attachedOn) bits.push(attachedOn);
  if (attributedTo) bits.push(`by ${attributedTo}`);

  return {
    state: 'holds',
    filename: present(current.filename),
    attachedOn,
    attributedTo,
    sourceLabel,
    versionLabel: present(current.versionLabel),
    detail: bits.join(' '),
    reason: '',
    canDownload: true,
    downloadVersionId: CURRENT_ATTACHMENT,
    earlierVersionCount,
    earlierVersionNote,
  };
}
